import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { StyleSheet, Pressable, View, ActivityIndicator, Linking, Platform, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useAuthStore, useToastStore, useModalStore } from '@/store';
import copyText from '@/constants/utils/copy_text';
import UpgradeContainer from '@/components/UpgradeContainer';
import { apiKeysApi } from '@/lib/api';
import getRelativeTime from '@/constants/utils/timestamp';
import { API_DAILY_LIMIT_PLUS_DISPLAY, API_DAILY_LIMIT_PREMIUM_DISPLAY } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import {
  BillingGroup,
  BillingRow,
  BillingSection,
  BillingTile,
  BillingValueRow,
  BILLING_ROW_ICON_INSET,
  useBillingPalette,
} from '@/components/Settings/billing/BillingGroup';

const BASE_URL = 'https://gab.ai/v1';
const MAX_ACTIVE_KEYS = 10;

const MONO = Platform.select({
  ios: 'Menlo',
  android: 'monospace',
  default: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
});

const CODE_EXAMPLES = {
  nodejs: `const OpenAI = require('openai');

const client = new OpenAI({
  apiKey: 'YOUR_API_KEY_HERE',
  baseURL: 'https://gab.ai/v1',
});

// Use GET /v1/models to retrieve the current model IDs.
// For example: 'arya' or 'gpt-5-4'
const response = await client.chat.completions.create({
  model: 'arya',
  messages: [
    { role: 'user', content: 'Hello, how are you?' }
  ]
});

console.log(response.choices[0].message.content);`,
  python: `from openai import OpenAI

client = OpenAI(
    api_key = "YOUR_API_KEY_HERE",
    base_url = "https://gab.ai/v1",
)

# Use GET /v1/models to retrieve the current model IDs.
# For example: 'arya' or 'gpt-5-4'
response = client.chat.completions.create(
    model="arya",
    messages=[
        {"role": "user", "content": "Hello, how are you?"}
    ]
)

print(response.choices[0].message.content)`,
};

const LANGUAGES = [
  { id: 'nodejs', label: 'Node.js', file: 'index.js', comment: '//', keywords: ['const', 'require', 'new', 'await'] },
  { id: 'python', label: 'Python', file: 'main.py', comment: '#', keywords: ['from', 'import'] },
];

const API_CAPABILITIES = [
  { icon: 'message',     label: 'Chat Completions', endpoint: '/v1/chat/completions' },
  { icon: 'image',       label: 'Image Generation', endpoint: '/v1/images/generations' },
  { icon: 'wand-magic-sparkles', label: 'Image Editing', endpoint: '/v1/images/edit' },
  { icon: 'video',       label: 'Video Generation', endpoint: '/v1/videos/generations' },
  { icon: 'volume-high', label: 'Text-to-Speech',   endpoint: '/v1/audio/speech' },
  { icon: 'vector-square', label: 'Embeddings',     endpoint: '/v1/embeddings' },
  { icon: 'coins',       label: 'Credit Balance',   endpoint: '/v1/credits' },
  { icon: 'key',         label: 'API Key Management', endpoint: '/v1/api-keys' },
  { icon: 'microchip',   label: 'List Models',      endpoint: '/v1/models' },
];

// The code surface stays dark in both appearances, like an editor pane.
const CODE_THEME = {
  surface: '#1c1c1e',
  bar: '#232326',
  border: 'rgba(255,255,255,0.08)',
  plain: 'rgba(255,255,255,0.86)',
  muted: 'rgba(255,255,255,0.45)',
  keyword: '#FF7AB2',
  string: '#FF8170',
  comment: '#7F8C98',
  number: '#D9C97C',
};

const tokenizeLine = (line, language) => {
  const commentAt = line.trimStart().startsWith(language.comment) ? line.indexOf(language.comment) : -1;
  if (commentAt >= 0) {
    return [
      { text: line.slice(0, commentAt), kind: 'plain' },
      { text: line.slice(commentAt), kind: 'comment' },
    ];
  }
  const keywordPattern = language.keywords.join('|');
  const pattern = new RegExp(`('[^']*'|"[^"]*"|\\b(?:${keywordPattern})\\b|\\b\\d+\\b)`, 'g');
  return line.split(pattern).filter(Boolean).map((text) => {
    if (/^['"]/.test(text)) return { text, kind: 'string' };
    if (/^\d+$/.test(text)) return { text, kind: 'number' };
    if (language.keywords.includes(text)) return { text, kind: 'keyword' };
    return { text, kind: 'plain' };
  });
};

const createStyles = (palette, colors) => StyleSheet.create({
  container: {
    flex: 1,
    gap: 28,
    paddingBottom: 16,
  },

  hero: {
    borderRadius: 18,
    backgroundColor: palette.isDark ? 'rgba(255,255,255,0.055)' : (colors?.solid_color_primary || '#fff'),
    borderColor: palette.isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)',
    ...(Platform.OS === 'web' && !palette.isDark
      ? { boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 12px 32px -12px rgba(0,0,0,0.10)' }
      : null),
  },
  heroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 16,
    paddingTop: 18,
    paddingBottom: 16,
  },
  heroTitle: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: palette.tileFill,
    flexShrink: 0,
  },
  pillDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#34C759',
  },
  baseUrlRow: {
    minHeight: 42,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    paddingLeft: 16,
    paddingRight: 10,
    paddingVertical: 6,
  },
  baseUrlValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
  },
  mono: {
    fontFamily: MONO,
  },
  iconButton: {
    width: 26,
    height: 26,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },

  keyRowRevoked: {
    opacity: 0.55,
  },
  keySubtitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  revokeButton: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 999,
  },
  revokedBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: palette.tileFill,
  },
  stateBlock: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  stateText: {
    alignItems: 'center',
    gap: 3,
  },
  primaryCapsule: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginTop: 4,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: palette.ink,
  },

  segmented: {
    flexDirection: 'row',
    padding: 2,
    borderRadius: 9,
    backgroundColor: palette.tileFill,
  },
  segment: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 7,
  },
  segmentActive: {
    backgroundColor: palette.isDark ? 'rgba(255,255,255,0.16)' : '#fff',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 2px rgba(0,0,0,0.10), 0 1px 1px rgba(0,0,0,0.04)' }
      : { shadowColor: '#000', shadowOpacity: 0.1, shadowRadius: 2, shadowOffset: { width: 0, height: 1 }, elevation: 1 }),
  },
  codeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: CODE_THEME.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: palette.isDark ? CODE_THEME.border : 'rgba(0,0,0,0.12)',
    ...(Platform.OS === 'web' && !palette.isDark
      ? { boxShadow: '0 12px 32px -16px rgba(0,0,0,0.35)' }
      : null),
  },
  codeBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 16,
    paddingRight: 8,
    paddingVertical: 6,
    backgroundColor: CODE_THEME.bar,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: CODE_THEME.border,
  },
  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 7,
  },
  codeBody: {
    paddingHorizontal: 18,
    paddingVertical: 16,
  },
  codeLine: {
    fontFamily: MONO,
    fontSize: 13,
    lineHeight: 21,
    color: CODE_THEME.plain,
  },
});

function APISettings() {
  const router = useRouter();
  const palette = useBillingPalette();
  const { colors } = palette;
  const styles = useMemo(() => createStyles(palette, colors), [palette, colors]);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const [activeTab, setActiveTab] = useState('nodejs');
  const { addToast } = useToastStore(state => state.actions);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const [apiKeys, setApiKeys] = useState([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(true);
  const [keysError, setKeysError] = useState(null);

  // Revoked keys do not count toward the 10-key limit.
  const activeKeyCount = useMemo(
    () => apiKeys.filter(k => k.isActive !== false).length,
    [apiKeys]
  );

  const fetchApiKeys = useCallback(async () => {
    setIsLoadingKeys(true);
    setKeysError(null);
    try {
      const keys = await apiKeysApi.list();
      setApiKeys(keys);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      setKeysError(error.message || 'Failed to load API keys');
    } finally {
      setIsLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    if (isPlus) fetchApiKeys();
  }, [isPlus, fetchApiKeys]);

  const handleCopyCode = async () => {
    try {
      await copyText(CODE_EXAMPLES[activeTab]);
      addToast(t('settings.api.codeCopied'), 'success');
    } catch (error) {
      console.error('Failed to copy code:', error);
      addToast(t('settings.api.codeCopyFailed'), 'error');
    }
  };

  const handleCopyBaseUrl = async () => {
    try {
      await copyText(BASE_URL);
      addToast(t('settings.api.baseUrlCopied'), 'success');
    } catch (error) {
      console.error('Failed to copy base URL:', error);
    }
  };

  const handleCreateKey = useCallback(() => {
    openModal('CreateAPIKeyModal', {
      onCreate: async (name) => {
        const result = await apiKeysApi.create(name);
        return result;
      },
      onKeyCreated: () => {
        fetchApiKeys();
        addToast(t('settings.api.keyCreated'), 'success');
      },
    });
  }, [openModal, fetchApiKeys, addToast, t]);

  const handleRevokeKey = useCallback((keyToRevoke) => {
    openModal('ConfirmationModal', {
      title: t('settings.api.revokeKey'),
      message: t('settings.api.revokeKeyMessage'),
      itemName: keyToRevoke.name || t('settings.api.unnamedKey'),
      confirmText: t('settings.api.revoke'),
      cancelText: t('common.cancel'),
      isDanger: true,
      onConfirm: async () => {
        try {
          await apiKeysApi.revoke(keyToRevoke.id);
          addToast(t('settings.api.keyRevoked'), 'success');
          fetchApiKeys();
          closeModal();
        } catch (error) {
          if (error.message?.includes('cannot_delete_current_key') || error.message?.includes('being used')) {
            addToast(t('settings.api.cannotRevokeCurrent'), 'error');
          } else {
            addToast(error.message || t('settings.api.revokeKeyFailed'), 'error');
          }
        }
      },
    });
  }, [openModal, closeModal, fetchApiKeys, addToast, t]);

  const formatDate = (timestamp) => {
    if (!timestamp) return t('settings.api.never');
    const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
    return getRelativeTime(date);
  };

  const hoverable = (base, hoverColor) => ({ hovered, pressed }) => [
    base,
    Platform.OS === 'web' && { cursor: 'pointer', transition: 'background-color 120ms ease' },
    hovered && { backgroundColor: hoverColor },
    pressed && { opacity: 0.7 },
  ];

  if (!isPlus) {
    return (
      <UpgradeContainer
        alignLeft
        noPadding
        title={t('settings.api.accessTitle')}
        subtitle={t('settings.api.accessSubtitle')}
        features={[
          {
            icon: 'code',
            title: t('settings.api.restfulAccess'),
            description: t('settings.api.restfulAccessDesc')
          },
          {
            icon: 'cube',
            title: t('settings.api.multipleModels'),
            description: t('settings.api.multipleModelsDesc')
          },
          {
            icon: 'gauge-high',
            title: t('settings.api.requestsPerDay'),
            description: t('settings.api.requestsPerDayDesc')
          },
        ]}
        plusFeature="api"
      />
    );
  }

  const dailyLimit = isPlusPremium ? API_DAILY_LIMIT_PREMIUM_DISPLAY : API_DAILY_LIMIT_PLUS_DISPLAY;

  const renderOverview = () => (
    <BillingSection footer={t('settings.api.rateLimitHeaders')}>
      <BillingGroup style={styles.hero}>
        <View key="header" style={styles.heroHeader}>
          <BillingTile
            icon="code"
            size={44}
            radius={11}
            iconSize={18}
            color={palette.inkInverse}
            backgroundColor={palette.ink}
          />
          <View style={styles.heroTitle}>
            <Text size="l" weight="bold" color="primary">{t('settings.api.heroTitle')}</Text>
            <Text size="s" color="secondary">{t('settings.api.heroSubtitle')}</Text>
          </View>
          {!isXS && (
            <View style={styles.pill}>
              <View style={styles.pillDot} />
              <Text size="xs" weight="semibold" color="primary">{t('settings.subscription.active')}</Text>
            </View>
          )}
        </View>
        <View key="base-url" style={styles.baseUrlRow}>
          <Text size="n" color="secondary">{t('settings.api.baseUrl')}</Text>
          <View style={styles.baseUrlValue}>
            <Text size="s" weight="medium" color="primary" style={styles.mono} numberOfLines={1}>{BASE_URL}</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('settings.api.copyBaseUrl')}
              onPress={handleCopyBaseUrl}
              style={hoverable(styles.iconButton, palette.hover)}
            >
              <Icon name="copy" size={12} color={colors?.text_color_secondary} />
            </Pressable>
          </View>
        </View>
        <BillingValueRow
          key="limit"
          label={t('settings.api.dailyLimit')}
          value={t('settings.api.requestsValue').replace('%COUNT%', dailyLimit)}
        />
        <BillingValueRow key="reset" label={t('settings.api.resets')} value={t('settings.api.midnightUtcDaily')} />
        <BillingRow
          key="docs"
          icon="book"
          title={t('settings.api.viewApiDocs')}
          chevron
          onPress={() => router.push('/docs/api')}
        />
      </BillingGroup>
    </BillingSection>
  );

  const renderKeyRow = (key) => {
    const revoked = key.isActive === false;
    return (
      <View key={key.id} style={revoked && styles.keyRowRevoked}>
        <BillingRow
          icon="key"
          title={key.name || t('settings.api.unnamedKey')}
          subtitle={(
            <View style={styles.keySubtitle}>
              <Text size="s" color="secondary" style={[styles.mono, { flexShrink: 1 }]} numberOfLines={1}>{key.key}</Text>
              {!isXS && (
                <Text size="s" color="tertiary" numberOfLines={1}>
                  {`· ${formatDate(key.createdAt || key.created)}`}
                </Text>
              )}
            </View>
          )}
          trailing={revoked ? (
            <View style={styles.revokedBadge}>
              <Text size="xxs" weight="semibold" color="secondary">{t('settings.api.revoked')}</Text>
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`${t('settings.api.revokeKey')}: ${key.name || t('settings.api.unnamedKey')}`}
              onPress={() => handleRevokeKey(key)}
              style={hoverable(styles.revokeButton, palette.isDark ? 'rgba(255,69,58,0.14)' : 'rgba(255,59,48,0.08)')}
            >
              <Text size="s" weight="medium" style={{ color: palette.destructive }}>{t('settings.api.revoke')}</Text>
            </Pressable>
          )}
        />
      </View>
    );
  };

  const renderKeys = () => {
    if (isLoadingKeys) {
      return (
        <BillingGroup>
          <View style={styles.stateBlock}>
            <ActivityIndicator size="small" color={colors?.text_color_secondary} />
            <Text size="s" color="secondary">{t('settings.api.loadingApiKeys')}</Text>
          </View>
        </BillingGroup>
      );
    }

    if (keysError) {
      return (
        <BillingGroup>
          <View style={styles.stateBlock}>
            <BillingTile icon="circle-exclamation" size={40} radius={20} iconSize={16} color={palette.destructive} />
            <Text size="s" color="secondary" style={{ textAlign: 'center' }}>{keysError}</Text>
            <Button variant="secondary" size="small" text={t('settings.api.retry')} onPress={fetchApiKeys} />
          </View>
        </BillingGroup>
      );
    }

    if (apiKeys.length === 0) {
      return (
        <BillingGroup>
          <View style={styles.stateBlock}>
            <BillingTile icon="key" size={44} radius={22} iconSize={16} color={colors?.text_color_secondary} />
            <View style={styles.stateText}>
              <Text size="n" weight="semibold" color="primary">{t('settings.api.noApiKeys')}</Text>
              <Text size="s" color="secondary" style={{ textAlign: 'center' }}>{t('settings.api.noApiKeysSubtitle')}</Text>
            </View>
            <Pressable
              accessibilityRole="button"
              onPress={handleCreateKey}
              style={({ hovered, pressed }) => [
                styles.primaryCapsule,
                Platform.OS === 'web' && { cursor: 'pointer', transition: 'opacity 120ms ease' },
                hovered && { opacity: 0.88 },
                pressed && { opacity: 0.75 },
              ]}
            >
              <Icon name="plus" size={12} color={palette.inkInverse} />
              <Text size="s" weight="semibold" style={{ color: palette.inkInverse }}>{t('settings.api.createNewKey')}</Text>
            </Pressable>
          </View>
        </BillingGroup>
      );
    }

    return (
      <BillingGroup inset={BILLING_ROW_ICON_INSET}>
        {apiKeys.map(renderKeyRow)}
        {activeKeyCount < MAX_ACTIVE_KEYS && (
          <BillingRow
            key="create"
            leading={<BillingTile icon="plus" color={palette.inkInverse} backgroundColor={palette.ink} />}
            title={t('settings.api.createNewKey')}
            onPress={handleCreateKey}
          />
        )}
      </BillingGroup>
    );
  };

  const renderLanguagePicker = () => (
    <View style={styles.segmented} accessibilityRole="tablist">
      {LANGUAGES.map((lang) => {
        const active = activeTab === lang.id;
        return (
          <Pressable
            key={lang.id}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            onPress={() => setActiveTab(lang.id)}
            style={[styles.segment, active && styles.segmentActive, Platform.OS === 'web' && { cursor: 'pointer' }]}
          >
            <Text size="xs" weight={active ? 'semibold' : 'medium'} color={active ? 'primary' : 'secondary'}>
              {lang.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );

  const renderCode = () => {
    const language = LANGUAGES.find(lang => lang.id === activeTab);
    const lines = CODE_EXAMPLES[activeTab].split('\n');
    return (
      <View style={styles.codeCard}>
        <View style={styles.codeBar}>
          <Text size="xs" weight="medium" style={[styles.mono, { color: CODE_THEME.muted }]}>{language.file}</Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('settings.api.copyCode')}
            onPress={handleCopyCode}
            style={hoverable(styles.copyButton, 'rgba(255,255,255,0.08)')}
          >
            <Icon name="copy" size={12} color={CODE_THEME.muted} />
            <Text size="xs" weight="medium" style={{ color: CODE_THEME.muted }}>{t('settings.api.copy')}</Text>
          </Pressable>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.codeBody}>
          <View>
            {lines.map((line, index) => (
              <Text key={index} style={styles.codeLine} selectable>
                {line.length === 0 ? ' ' : tokenizeLine(line, language).map((token, i) => (
                  <Text
                    key={i}
                    style={[
                      styles.codeLine,
                      token.kind !== 'plain' && { color: CODE_THEME[token.kind] },
                      token.kind === 'keyword' && { fontWeight: '600' },
                    ]}
                  >
                    {token.text}
                  </Text>
                ))}
              </Text>
            ))}
          </View>
        </ScrollView>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {renderOverview()}

      <BillingSection
        title={t('settings.api.apiKeys')}
        accessory={(
          <Text size="s" color="secondary">
            {t('settings.api.keysUsed').replace('%COUNT%', activeKeyCount).replace('%MAX%', MAX_ACTIVE_KEYS)}
          </Text>
        )}
        footer={apiKeys.length > 0 ? t('settings.api.securityNote') : null}
      >
        {renderKeys()}
      </BillingSection>

      <BillingSection
        title={t('settings.api.quickStart')}
        accessory={renderLanguagePicker()}
        footer={t('settings.api.apiNote')}
      >
        {renderCode()}
      </BillingSection>

      <BillingSection
        title={t('settings.api.endpoints')}
        accessory={<Text size="s" color="secondary">{API_CAPABILITIES.length}</Text>}
      >
        <BillingGroup inset={BILLING_ROW_ICON_INSET}>
          {API_CAPABILITIES.map(cap => (
            <BillingRow
              key={cap.endpoint}
              icon={cap.icon}
              title={cap.label}
              subtitle={isXS ? (
                <Text size="xs" color="tertiary" style={styles.mono} numberOfLines={1}>{cap.endpoint}</Text>
              ) : undefined}
              trailing={!isXS ? (
                <Text size="s" color="tertiary" style={styles.mono}>{cap.endpoint}</Text>
              ) : undefined}
            />
          ))}
        </BillingGroup>
      </BillingSection>

      <BillingGroup>
        <BillingRow
          icon="building"
          title={t('settings.api.enterprise')}
          subtitle={t('settings.api.enterpriseSubtitle')}
          trailing={!isXS ? <Text size="s" color="secondary">ai@gab.com</Text> : undefined}
          chevron
          accessibilityLabel={`${t('settings.api.enterprise')}: ai@gab.com`}
          onPress={() => Linking.openURL('mailto:ai@gab.com')}
        />
      </BillingGroup>
    </View>
  );
}

export default React.memo(APISettings);
